import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
import { Provider } from "react-redux";
import { PersistGate } from 'redux-persist/integration/react';
import { store, persistor } from "./store";
import { Toaster } from "sonner";
import { Login } from "./screens/Login/Login";
import { Otp } from "./screens/Otp/Otp";
import { MyPredictions } from "./screens/MyPredictions/MyPredictions";
import { CreateRecommendation } from "./screens/CreateRecommendation/CreateRecommendation";
import { MyRecommendation } from "./screens/MyRecommendation";
import { Settings } from "./screens/Settings/Settings";
import { EditProfile } from "./screens/EditProfile/EditProfile";
import { AboutUs } from "./screens/AboutUs/AboutUs";
import { Register } from "./screens/Register/Register";
import ProtectedRoute from "./components/ProtectedRoute";
import './i18n'; // Import i18n configuration
import { AuthGuard } from "./components/AuthGuard";
import CountryGuard from "./components/CountryGuard";
import NotFound from "./components/NotFound";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

// Absolute URL normalization: force any uppercase two-letter country code in the URL to lowercase before React renders
if (typeof window !== "undefined") {
  const match = window.location.pathname.match(/^\/([A-Z]{2})(\/|$)/);
  if (match) {
    const lower = match[1].toLowerCase();
    const newPath = window.location.pathname.replace(/^\/[A-Z]{2}/, `/${lower}`);
    
    // Safe redirect: newPath is internal and validated; not based on user input

    if (window.location.pathname !== newPath) {
      window.location.replace(newPath + window.location.search + window.location.hash); // NOSONAR
    }
  }
}

// Ensure the store is properly initialized
persistor.persist();

function determineCountry() {
  // You can improve this logic as needed
  const lang = navigator.language;
  if (lang.startsWith('bn')) return 'bd';
  if (lang.startsWith('th')) return 'th';
  if (lang.startsWith('id')) return 'id';
  if (lang.startsWith('vi')) return 'vn';
  if (lang.startsWith('lk')) return 'lk';
  if (lang.startsWith('my')) return 'my';
  return 'in'; // default
}

function LoginRedirector() {
  const navigate = useNavigate();
  useEffect(() => {
    // Try to get last country from localStorage
    const lastCountry = localStorage.getItem('lastCountry');
    const country = lastCountry || determineCountry();
    navigate(`/${country}/login`, { replace: true });
  }, [navigate]);
  return null;
}

createRoot(document.getElementById("app") as HTMLElement).render(
  <StrictMode>
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <Toaster position="top-right" />
        <Router>
          <AuthGuard>
            <Routes>
              {/* Country-prefixed routes guarded by CountryGuard */}
              <Route path=":country/*" element={<CountryGuard />} caseSensitive={false}>
                <Route path="login" element={<Login />} caseSensitive={false} />
                <Route path="otp" element={<Otp />} />
                <Route path="enter-name" element={<Register />} />
                <Route path="my-predictions" element={<ProtectedRoute><MyPredictions /></ProtectedRoute>} />
                <Route path="prediction-details" element={<ProtectedRoute><MyRecommendation /></ProtectedRoute>} />
                <Route path="new-prediction" element={<ProtectedRoute><CreateRecommendation /></ProtectedRoute>} />
                <Route path="settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                <Route path="edit-profile" element={<ProtectedRoute><EditProfile /></ProtectedRoute>} />
                <Route path="about-us" element={<ProtectedRoute><AboutUs /></ProtectedRoute>} />
                {/* NotFound for any other country-prefixed route */}
                <Route path="*" element={<NotFound />} />
              </Route>
              {/* Support /login and redirect to /:country/login */}
              <Route path="/login" element={<LoginRedirector />} />
              {/* Optionally, redirect / to /login */}
              <Route path="/" element={<LoginRedirector />} />
              {/* Catch-all: NotFound */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </AuthGuard>
        </Router>
      </PersistGate>
    </Provider>
  </StrictMode>
);